import { useState } from 'react'
import { BadgeCheck, Eye, Plus, ScanLine, Trash2 } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FileUploader, FormDialog, type UploadedFile,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toApiProblem } from '@/lib/api/client'
import { ExtractionDialog } from '@/features/ai'
import { DocumentStatusBadge } from './DocumentStatusBadge'
import {
  useDocumentTypes, useEmployeeDocumentMutations, useEmployeeDocuments, useRevealDocumentNumber,
} from '../api'
import type { EmployeeDocumentView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface Draft {
  documentTypeId: string
  documentNumber: string
  issuingCountry: string
  issuingAuthority: string
  issueDate: string
  expiryDate: string
  storedFileId: string | null
  notes: string
}

const EMPTY: Draft = {
  documentTypeId: '',
  documentNumber: '',
  issuingCountry: '',
  issuingAuthority: '',
  issueDate: '',
  expiryDate: '',
  storedFileId: null,
  notes: '',
}

/**
 * The documents on file for one person.
 *
 * <p>Numbers are not shown. The list endpoint does not return them and nothing here asks
 * for one until somebody presses Reveal — which is a logged read, and is told so before it
 * happens. A screen that renders every passport number on load turns the access log into a
 * record of page views.
 */
export function EmployeeDocumentsTab({ employeeId }: { employeeId: string }) {
  const documents = useEmployeeDocuments(employeeId)
  const types = useDocumentTypes('EMPLOYEE')
  const { save, verify, remove } = useEmployeeDocumentMutations(employeeId)
  const reveal = useRevealDocumentNumber()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRemove, setToRemove] = useState<EmployeeDocumentView | null>(null)
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [extracting, setExtracting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedType = types.data?.find((type) => type.id === draft?.documentTypeId)

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await save.mutateAsync({
        documentTypeId: draft.documentTypeId,
        documentNumber: draft.documentNumber || null,
        issuingCountry: draft.issuingCountry || null,
        issuingAuthority: draft.issuingAuthority || null,
        issueDate: draft.issueDate || null,
        expiryDate: draft.expiryDate || null,
        storedFileId: draft.storedFileId,
        notes: draft.notes || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That document could not be saved')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {/* Offered even when extraction is off or unusable: the dialog says which of those it
            is and why, which is more useful than a button that is not there at all. */}
        <Button type="button" variant="outline" onClick={() => setExtracting(true)}>
          <ScanLine className="size-4" aria-hidden="true" />
          Read from a scan
        </Button>
        <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
          <Plus className="size-4" aria-hidden="true" />
          Record a document
        </Button>
      </div>

      <ExtractionDialog
        open={extracting}
        onOpenChange={setExtracting}
        employeeId={employeeId}
        types={types.data ?? []}
      />

      <Card>
        {documents.isPending && <Skeleton className="m-4 h-24" />}
        {documents.isError && (
          <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
        )}
        {documents.isSuccess && documents.data.length === 0 && (
          <EmptyState
            title="Nothing on file"
            description="Record a passport, visa or labour card so its expiry is tracked."
          />
        )}
        {(documents.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[46rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Document</th>
                  <th scope="col" className="p-3 font-medium">Number</th>
                  <th scope="col" className="p-3 font-medium">Expires</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {documents.data?.map((document) => (
                  <tr key={document.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      {document.documentTypeName}
                      {document.verifiedAt && (
                        <BadgeCheck
                          className="ms-1.5 inline size-4 text-[var(--color-success)]"
                          aria-label="Checked against the original"
                        />
                      )}
                    </td>
                    <td className="p-3">
                      {revealed[document.id] ? (
                        <code className="text-xs">{revealed[document.id]}</code>
                      ) : (
                        <Button
                          type="button" size="sm" variant="ghost"
                          disabled={reveal.isPending}
                          onClick={() =>
                            reveal.mutate(
                              { id: document.id, reason: 'Viewed on the employee record' },
                              {
                                onSuccess: (full) =>
                                  setRevealed((current) => ({
                                    ...current,
                                    [document.id]: full.documentNumber ?? '—',
                                  })),
                              },
                            )
                          }
                        >
                          <Eye className="size-3.5" aria-hidden="true" />
                          Reveal
                        </Button>
                      )}
                    </td>
                    <td className="p-3 tabular-nums">{document.expiryDate ?? '—'}</td>
                    <td className="p-3">
                      <DocumentStatusBadge
                        status={document.status}
                        daysUntilExpiry={document.daysUntilExpiry}
                        withPhrase
                      />
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        {!document.verifiedAt && (
                          <Button
                            type="button" size="sm" variant="ghost"
                            onClick={() => void verify.mutateAsync(document.id)}
                          >
                            Mark checked
                          </Button>
                        )}
                        <Button
                          type="button" size="icon" variant="ghost"
                          aria-label={`Remove ${document.documentTypeName}`}
                          onClick={() => setToRemove(document)}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-[var(--color-muted)]">
        Revealing a number is recorded in the sensitive-access log, with who read it and why.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => !open && setDraft(null)}
        title="Record a document"
        description="Recording a replacement supersedes the one already on file — a person has one passport, not two."
        loading={save.isPending}
        error={error}
        onSubmit={() => void submit()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="doc-type">Document</Label>
              <Select
                id="doc-type"
                required
                value={draft.documentTypeId}
                onChange={(event) => setDraft({ ...draft, documentTypeId: event.target.value })}
                className="w-full"
              >
                <option value="">Choose a document type…</option>
                {(types.data ?? []).filter((type) => type.active).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="doc-number">
                Number{selectedType?.requiresNumber ? '' : ' (optional)'}
              </Label>
              <Input
                id="doc-number"
                required={selectedType?.requiresNumber}
                value={draft.documentNumber}
                onChange={(event) => setDraft({ ...draft, documentNumber: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-expiry">
                Expires{selectedType?.requiresExpiry ? '' : ' (optional)'}
              </Label>
              <DatePicker
                id="doc-expiry"
                required={selectedType?.requiresExpiry}
                value={draft.expiryDate}
                onChange={(event) => setDraft({ ...draft, expiryDate: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-issued">Issued</Label>
              <DatePicker
                id="doc-issued" value={draft.issueDate}
                onChange={(event) => setDraft({ ...draft, issueDate: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="doc-country">Issuing country</Label>
              <Input
                id="doc-country" maxLength={2} placeholder="AE" value={draft.issuingCountry}
                onChange={(event) => setDraft({ ...draft, issuingCountry: event.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="doc-authority">Issuing authority</Label>
              <Input
                id="doc-authority" value={draft.issuingAuthority}
                onChange={(event) => setDraft({ ...draft, issuingAuthority: event.target.value })}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>Scan{selectedType?.requiresFile ? '' : ' (optional)'}</Label>
              <FileUploader
                ownerType="employee_document"
                ownerId={employeeId}
                onUploaded={(file: UploadedFile) => setDraft({ ...draft, storedFileId: file.id })}
                onRemoved={() => setDraft({ ...draft, storedFileId: null })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRemove != null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={`Remove ${toRemove?.documentTypeName ?? 'this document'}?`}
        description="The record is kept in the history and the scan is not deleted; it simply stops counting as on file."
        confirmLabel="Remove"
        onConfirm={() => {
          if (toRemove) {
            remove.mutate(toRemove.id, { onSettled: () => setToRemove(null) })
          }
        }}
      />
    </div>
  )
}
